/**
 * MechaGuard - TeslaTower (特斯拉塔)
 *
 * 攻击方式：瞬发连锁闪电，命中主目标后向最近的未命中敌人弹射。
 * 定位：专门克制密集队形，单体 DPS 不高但群体总伤很高。
 * 满级特性：弹射次数与弹射距离提升。
 */

import { Tower } from '../entities/Tower.js';

export class TeslaTower extends Tower {
  static META = {
    id: 'tesla',
    name: '特斯拉塔',
    icon: '⚛️',
    cost: 200,
    desc: '连锁闪电，弹射多个目标',
    color: '#c04bff',
    stats: {
      baseRange: 122,
      baseDamage: 22,
      fireInterval: 0.9,   // ≈ 24.4 DPS（单体）
      turnSpeed: 14,
      firingFx: 0.12,
      chainCount: 3,        // 额外弹射目标数
      chainRange: 92,       // 弹射搜索半径
      chainFalloff: 0.62,   // 每次弹射伤害衰减
      chainCountBonus: 2,   // 满级额外弹射数
      chainRangeMul: 1.25,  // 满级弹射半径乘数
    },
  };

  constructor(col, row) {
    super(col, row);
  }

  get effectiveChainCount() {
    return this.level >= 3 ? this.chainCount + this.chainCountBonus : this.chainCount;
  }

  get effectiveChainRange() {
    return this.level >= 3 ? this.chainRange * this.chainRangeMul : this.chainRange;
  }

  update(dt, game) {
    this.tickTimers(dt);

    const target = this.findTarget(game.enemies);
    this.target = target;

    if (!target) return;

    this.aimAt(target, dt, this.turnSpeed);

    if (this.cooldown > 0) return;

    this.cooldown = this.fireInterval;
    this.firing = this.firingFx;
    game.sfx('tesla');

    this._fireChain(game, target);
  }

  /**
   * 计算连锁路径并统一结算伤害
   * （先确定全部目标再造成伤害，避免中途目标死亡影响弹射链）
   */
  _fireChain(game, first) {
    const points = [
      { x: this.x, y: this.y },
      { x: first.x, y: first.y },
    ];

    /** @type {{enemy: import('../entities/Enemy.js').Enemy, damage: number}[]} */
    const hits = [{ enemy: first, damage: this.damage }];
    const hitSet = new Set([first]);

    const maxChain = this.effectiveChainCount;
    const rangeSq = this.effectiveChainRange * this.effectiveChainRange;

    let current = first;
    let chainDamage = this.damage;

    for (let i = 0; i < maxChain; i++) {
      chainDamage *= this.chainFalloff;
      if (chainDamage < 1) break;

      let next = null;
      let bestSq = rangeSq;

      for (let j = 0; j < game.enemies.length; j++) {
        const e = game.enemies[j];
        if (!e.alive || e.reachedBase || hitSet.has(e)) continue;

        const d = e.distanceSqTo(current.x, current.y);
        if (d <= bestSq) {
          bestSq = d;
          next = e;
        }
      }

      if (!next) break;

      hitSet.add(next);
      hits.push({ enemy: next, damage: chainDamage });
      points.push({ x: next.x, y: next.y });
      current = next;
    }

    for (let i = 0; i < hits.length; i++) {
      const dealt = game.damageEnemy(hits[i].enemy, hits[i].damage);
      this.totalDamage += dealt;
    }

    game.effects.addChain(points, this.color, 0.18);
    game.particles.spawnBurst(first.x, first.y, {
      count: 8,
      color: this.color,
      speed: 90,
      life: 0.35,
      size: 2.4,
    });
  }

  drawTurret(ctx, accent) {
    // 球形线圈
    ctx.fillStyle = 'rgba(10, 24, 42, 0.92)';
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.arc(0, 0, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // 三根电极
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;
    for (let i = 0; i < 3; i++) {
      const a = this.age * 2 + (Math.PI * 2 / 3) * i;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * 6, Math.sin(a) * 6);
      ctx.lineTo(Math.cos(a) * 14, Math.sin(a) * 14);
      ctx.stroke();
    }

    // 电弧核心
    ctx.shadowColor = accent;
    ctx.shadowBlur = 14;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(0, 0, 3.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    // 满级标记：额外外环
    if (this.level >= 3) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 4]);
      ctx.beginPath();
      ctx.arc(0, 0, 15, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }
}
